import { z } from 'zod';
import { IdpSchema, OrgRoleSchema, PlanSchema, type Idp } from './enums.js';
import { UserSummarySchema } from './auth.js';

/** Identity providers an organisation may restrict sign-in to (never `dev`). */
export const ORG_ALLOWED_IDPS = ['microsoft', 'google'] as const;
export const OrgAllowedIdpSchema = z.enum(ORG_ALLOWED_IDPS);
export type OrgAllowedIdp = z.infer<typeof OrgAllowedIdpSchema>;

/** Display names for the providers above (login buttons, settings, errors). */
export const IDP_LABELS: Record<OrgAllowedIdp, string> = {
  microsoft: 'Microsoft 365',
  google: 'Google Workspace',
};

/** Display name for any provider a user may have signed in with, including `dev`. */
export function idpLabel(idp: Idp): string {
  return idp === 'dev' ? 'Development' : IDP_LABELS[idp];
}

export const OrgSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  domain: z.string().nullable(),
  allowDomainJoin: z.boolean(),
  /** Providers members may sign in with; null = any. */
  allowedIdps: z.array(z.string()).nullable(),
  logoUrl: z.string().nullable(),
  /** Organisation-level switch for every AI feature (migration 0014). */
  aiEnabled: z.boolean(),
  plan: PlanSchema,
  status: z.enum(['active', 'suspended']),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Org = z.infer<typeof OrgSchema>;

export const CreateOrgInputSchema = z.object({
  name: z.string().min(2).max(200),
  slug: z
    .string()
    .min(2)
    .max(60)
    .regex(/^[a-z0-9][a-z0-9-]*$/)
    .optional(),
});
export type CreateOrgInput = z.infer<typeof CreateOrgInputSchema>;

export const UpdateOrgInputSchema = z.object({
  name: z.string().min(2).max(200).optional(),
  logoUrl: z.string().url().nullable().optional(),
  allowDomainJoin: z.boolean().optional(),
  /** Restrict sign-in to these providers (at least one); null lifts the restriction. */
  allowedIdps: z.array(OrgAllowedIdpSchema).min(1).nullable().optional(),
  /** Turn every AI feature on or off for this organisation. */
  aiEnabled: z.boolean().optional(),
});
export type UpdateOrgInput = z.infer<typeof UpdateOrgInputSchema>;

export const UserStatusSchema = z.enum(['active', 'disabled']);
export type UserStatus = z.infer<typeof UserStatusSchema>;

export const OrgMemberSchema = z.object({
  orgId: z.string(),
  user: UserSummarySchema,
  role: OrgRoleSchema,
  /** Provider the member signs in with (`dev` only outside production). */
  idp: IdpSchema,
  /** Platform-level account status; a disabled user cannot sign in anywhere. */
  status: UserStatusSchema,
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type OrgMember = z.infer<typeof OrgMemberSchema>;

export const UpdateMemberInputSchema = z.object({
  role: OrgRoleSchema,
});
export type UpdateMemberInput = z.infer<typeof UpdateMemberInputSchema>;

export const InvitationSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  email: z.string(),
  role: OrgRoleSchema,
  invitedBy: z.string(),
  expiresAt: z.string(),
  acceptedAt: z.string().nullable(),
  createdAt: z.string(),
});
export type Invitation = z.infer<typeof InvitationSchema>;

export const CreateInvitationInputSchema = z.object({
  email: z.string().email(),
  role: OrgRoleSchema,
});
export type CreateInvitationInput = z.infer<typeof CreateInvitationInputSchema>;

/** Returned once at creation time — contains the one-time acceptance token. */
export const CreatedInvitationSchema = InvitationSchema.extend({
  token: z.string(),
  acceptUrl: z.string(),
});
export type CreatedInvitation = z.infer<typeof CreatedInvitationSchema>;

export const AcceptInvitationInputSchema = z.object({
  token: z.string().min(10),
});
export type AcceptInvitationInput = z.infer<typeof AcceptInvitationInputSchema>;

export const AssessorAssignmentSchema = z.object({
  id: z.string(),
  orgId: z.string(),
  user: UserSummarySchema,
  assessmentId: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  revokedAt: z.string().nullable(),
});
export type AssessorAssignment = z.infer<typeof AssessorAssignmentSchema>;

export const CreateAssessorInputSchema = z.object({
  email: z.string().email(),
  assessmentId: z.string().nullable().optional(),
});
export type CreateAssessorInput = z.infer<typeof CreateAssessorInputSchema>;
